import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import { clickAnimated, delay } from '@/utils/tour/actions';
import { logger } from '@/utils/logger';
import { waitForCondition, waitForTourElement } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for GAIA Chat: where to ask, how to pick the model
 * that answers, and how the thread history brings back earlier
 * conversations to continue. The demo NEVER sends a message (a real AI
 * call); it only peeks the model menu, flips the history open, and reopens
 * the user's own most recent thread — then "New chat" restores a clean
 * slate when the tour ends.
 */

const isVisible = (el: HTMLElement | null): boolean =>
  el !== null && el.getClientRects().length > 0;

/** Open the model menu and leave it open — the next scene explains it. */
async function openModelMenu(ctx: TourActionContext): Promise<void> {
  if (isVisible(document.querySelector<HTMLElement>('[data-tour="chat.model-menu"]'))) {
    return;
  }
  const trigger = await waitForTourElement('chat.model-button', 3000);
  if (!trigger || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(trigger, ctx);
  await waitForCondition(() => document.querySelector('[data-tour="chat.model-menu"]'), 3000);
}

/** Close the model menu again (the trigger button toggles it). */
async function closeModelMenu(ctx: TourActionContext): Promise<void> {
  if (!isVisible(document.querySelector<HTMLElement>('[data-tour="chat.model-menu"]'))) {
    return;
  }
  const trigger = document.querySelector<HTMLElement>('[data-tour="chat.model-button"]');
  if (!trigger || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(trigger, ctx);
  await delay(300);
}

/**
 * Enter chat mode. On the home screen the composer sits under the greeting and
 * the thread header (History / New chat) does not exist yet, so the scenes that
 * need it open the chat first.
 */
async function openChat(ctx: TourActionContext): Promise<void> {
  if (isVisible(document.querySelector<HTMLElement>('[data-tour="chat.history"]'))) {
    return;
  }
  const trigger = await waitForTourElement('chat.open-chat', 3000);
  if (!trigger || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(trigger, ctx);
  await waitForCondition(() => document.querySelector('[data-tour="chat.history"]'), 3000);
}

/** Open the thread history modal (no-op when it is already showing). */
async function openThreadHistory(ctx: TourActionContext): Promise<void> {
  await openChat(ctx);
  if (ctx.isCancelled()) {
    return;
  }
  if (isVisible(document.querySelector<HTMLElement>('[data-tour="chat.threads"]'))) {
    return;
  }
  const toggle = await waitForTourElement('chat.history', 3000);
  if (!toggle || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(toggle, ctx);
  await waitForCondition(() => document.querySelector('[data-tour="chat.threads"]'), 3000);
}

// Whether the demo actually reopened a thread — set synchronously by the
// action, so the follow-up scene skips instantly (no target timeout) when
// there was no thread to click.
let threadOpened = false;

/**
 * Reopen the user's most recent conversation from the history list. With no
 * threads yet, this quietly does nothing and the follow-up scene skips
 * (its target, the composer, only renders outside the list view).
 */
async function openMostRecentThread(ctx: TourActionContext): Promise<void> {
  threadOpened = false;
  const panel = await waitForTourElement('chat.threads', 3000);
  if (!panel || ctx.isCancelled()) {
    return;
  }
  const item = await waitForCondition(
    () => panel.querySelector<HTMLElement>('.thread-list-item'),
    3000
  );
  if (!item || ctx.isCancelled()) {
    logger.warn('[tour] no chat threads to reopen — skipping the thread hop');
    return;
  }
  ctx.setHighlight(item);
  if (ctx.animate) {
    await delay(700);
  }
  await clickAnimated(item, ctx);
  threadOpened = !ctx.isCancelled();
  // The restored conversation brings the composer back.
  await waitForCondition(() => document.querySelector('[data-tour="chat.composer"]'), 3000);
}

/**
 * End-of-tour cleanup: the demo may have left an old thread open. The chat is
 * the home screen now, and its open conversation lives in the query — so
 * dropping the query is the clean slate, no button press required. Guarded to
 * home: the same anchors exist in embedded chats (review projects).
 */
async function resetToNewChat(): Promise<void> {
  if (getRouter().currentRoute.value.name !== 'home') {
    return;
  }
  await getRouter().push({ query: {} });
  await delay(300);
}

export const chatTour: TourDefinition = {
  key: 'gaia-chat',
  title: 'GAIA Chat',
  description: 'Ask, pick your model, and pick up old conversations.',
  entryRoute: 'home',
  onComplete: resetToNewChat,
  onExit: resetToNewChat,
  steps: [
    {
      id: 'composer',
      route: 'home',
      target: 'chat.composer',
      placement: 'top',
      title: 'Ask GAIA anything',
      body:
        'Legal questions, document analysis, drafting help: type it here ' + 'and GAIA answers.',
    },
    {
      // Highlight only — clicking would open the native file picker, which the
      // tour cannot dismiss.
      id: 'attach',
      target: 'chat.attach',
      placement: 'top-start',
      title: 'Ask about your documents',
      body:
        'Attach files with the paperclip or drop them right onto the ' +
        'composer. They are stored in your vault, and once processed, GAIA ' +
        'answers from their contents. You can also start from the vault or ' +
        'a contract: look for the "Ask AI" action.',
    },
    {
      id: 'model',
      target: 'chat.model-button',
      placement: 'top-start',
      title: 'Mode and model',
      body:
        'This is also where you set how much GAIA checks in with you ' +
        'before acting, and which AI model answers. Let me open the menu.',
      actions: [{ kind: 'custom', run: openModelMenu }],
    },
    {
      id: 'model-menu',
      target: 'chat.model-menu',
      placement: 'right',
      title: 'Confirmation mode and models',
      body:
        'Up top, pick how cautious GAIA should be: confirm changes ' +
        'before they run, or run freely. Below, pick a provider and ' +
        'model: quick answers or deep analysis, whatever the task needs. ' +
        'I will close it and move on.',
      // Closing the menu also opens the chat: the next scene targets the
      // thread header, which only exists once the composer has morphed.
      actions: [
        { kind: 'custom', run: closeModelMenu },
        { kind: 'custom', run: openChat },
      ],
      // Back must not leave the menu floating over the previous scene.
      backActions: [{ kind: 'custom', run: closeModelMenu }],
    },
    {
      id: 'history',
      target: 'chat.history',
      placement: 'bottom-end',
      title: 'Your chat history',
      body: 'Every chat is saved as a thread. I will open the history so you ' + 'can see them.',
      actions: [{ kind: 'custom', run: openThreadHistory }],
    },
    {
      id: 'threads',
      target: 'chat.threads',
      placement: 'right',
      title: 'Go back any time',
      body:
        'Need to check something or continue where you left off? Click a ' +
        'thread and the whole conversation comes back. I will open the ' +
        'most recent one.',
      actions: [{ kind: 'custom', run: openMostRecentThread }],
    },
    {
      id: 'thread-restored',
      prerequisite: () => threadOpened,
      target: 'chat.composer',
      placement: 'top',
      title: 'Right where you left off',
      body:
        'The full conversation is restored, and the composer is ready: ' +
        'keep asking in the same context.',
    },
    {
      id: 'finale',
      title: 'That is GAIA Chat',
      body:
        'Ask in the composer, attach documents to ask about them, switch ' +
        'models to match the task, and pull any old thread back from the ' +
        'history. New chat starts you fresh.',
    },
  ],
};
